import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  Button,
  CircularProgress,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import CmsContext from "../../store/cms-context";
import { getAccessJwtToken } from "../../store/globalUtilities";
import Currencies from "../../Utilities/Currencies";
import currencySymbols from "../../Utilities/CurrencieSymbols";
import { has_admin_access, has_finance_access, has_finance_write_access } from "../../Utilities/HelperFunctions";
function AnalyticsConversionRates(props) {
  // Always call hooks first.
  const cmsContext = useContext(CmsContext);
  const currentUser = cmsContext.current_user;
  const canView = has_admin_access(currentUser) || has_finance_access(currentUser);
  const canWrite = has_admin_access(currentUser) || has_finance_write_access(currentUser);
  
  const [isLoading, setIsLoading] = useState(false);
  const [conversionRates, setConversionRates] = useState({});
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const [conversionRate, setConversionRate] = useState("0.000000");
  const [targetCurrency, setTargetCurrency] = useState("GBP");

  // Call useEffect unconditionally. Inside, perform permission checks if needed.
  useEffect(() => {
    if (!canView) {
      // User cannot view; do not attempt any fetch.
      return;
    }
    console.log("Component mounted: AnalyticsConversionRates");
    fetchConversionRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  function fetchConversionRates() {
    console.log("Fetching current conversion rates...");
    setIsLoading(true);

    getAccessJwtToken()
      .then((auth_creds) => {
        return fetch(
          "https://fri7xzt2n6.execute-api.eu-west-2.amazonaws.com/SnagCMS/currency_conversions",
          {
            method: "GET",
            headers: {
              Authorization: auth_creds.jwt,
              email: cmsContext.current_shop.email,
              "content-type": "application/json",
            },
          }
        );
      })
      .then((res) => {
        console.log("Fetch response status:", res.status);
        return res.json();
      })
      .then((result) => {
        console.log("API response received:", result);
        setIsLoading(false);

        if (result.conversion_rates) {
          setConversionRates(result.conversion_rates);
        } else {
          cmsContext.setSnackBarOpen(true, "error", "Failed to fetch conversion rates.");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Error fetching conversion rates:", error);
        cmsContext.setSnackBarOpen(true, "error", "Error fetching conversion rates.");
      });
  }

  // Write actions are only allowed if the user has write access.
  function handleSaveConversionRate() {
    if (!canWrite) return;

    console.log("Saving conversion rate...");
    const requestBody = {
      [`${selectedCurrency}_TO_${targetCurrency}`]: parseFloat(conversionRate).toFixed(6),
      request_type: "POST",
    };

    console.log("Request Body:", JSON.stringify(requestBody));

    getAccessJwtToken()
      .then((auth_creds) => {
        return fetch(
          "https://fri7xzt2n6.execute-api.eu-west-2.amazonaws.com/SnagCMS/currency_conversions",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: auth_creds.jwt,
              email: cmsContext.current_shop.email,
            },
            body: JSON.stringify(requestBody),
          }
        );
      })
      .then((res) => res.json())
      .then((result) => {
        if (result.message) {
          cmsContext.setSnackBarOpen(true, "success", "Conversion rate saved successfully.");
          fetchConversionRates(); // Refresh data after update
        } else {
          cmsContext.setSnackBarOpen(true, "error", "Error saving conversion rate.");
        }
      })
      .catch((error) => {
        console.error("Error saving conversion rate:", error);
        cmsContext.setSnackBarOpen(true, "error", "Error saving conversion rate.");
      });
  }

  function handleEditConversion(pair, rate) {
    if (!canWrite) return; // Only allow editing if user has write access

    const parts = pair.split("_TO_");
    if (parts.length === 2) {
      setSelectedCurrency(parts[0]);
      setTargetCurrency(parts[1]);
      setConversionRate(rate.toString());
    }
  }

  function handleDeleteConversion(pair) {
    if (!canWrite) return; // Only allow deletion if user has write access

    console.log("Deleting conversion rate for:", pair);
    const requestBody = {
      [`${pair}`]: null,
      request_type: "DELETE",
    };

    getAccessJwtToken()
      .then((auth_creds) => {
        return fetch(
          "https://fri7xzt2n6.execute-api.eu-west-2.amazonaws.com/SnagCMS/currency_conversions",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: auth_creds.jwt,
              email: cmsContext.current_shop.email,
            },
            body: JSON.stringify(requestBody),
          }
        );
      })
      .then((res) => res.json())
      .then((result) => {
        if (result.message) {
          cmsContext.setSnackBarOpen(true, "success", "Conversion rate deleted successfully.");
          fetchConversionRates(); // Refresh data after deletion
        } else {
          cmsContext.setSnackBarOpen(true, "error", "Error deleting conversion rate.");
        }
      })
      .catch((error) => {
        console.error("Error deleting conversion rate:", error);
        cmsContext.setSnackBarOpen(true, "error", "Error deleting conversion rate.");
      });
  }

  // Now conditionally render the UI.
  // Since all hooks have been called unconditionally above,
  // it is safe to return early here if the user doesn't have view permissions.
  if (!canView) {
    return null;
  }

  return (
    <Box component="main" sx={{ flexGrow: 1, py: 8 }}>
      <Container maxWidth={false} style={{ maxWidth: "1000px" }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={2}>
              <Typography variant="h5">Manage Conversion Rates</Typography>
            </Stack>
          </Grid>

          {/* Render form controls only if the user has write permission */}
          {canWrite && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="From Currency"
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                >
                  {Object.entries(Currencies).map(([code, name]) => (
                    <MenuItem key={code} value={code}>
                      {name} ({code})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Conversion Rate"
                  type="number"
                  inputProps={{ step: "0.000001" }}
                  value={conversionRate}
                  onChange={(e) => setConversionRate(e.target.value)}
                  InputProps={{
                    startAdornment: currencySymbols.get(selectedCurrency) || "",
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="To Currency"
                  value={targetCurrency}
                  onChange={(e) => setTargetCurrency(e.target.value)}
                >
                  {Object.entries(Currencies).map(([code, name]) => (
                    <MenuItem key={code} value={code}>
                      {name} ({code})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <Button variant="contained" onClick={handleSaveConversionRate}>
                  Save Conversion Rate
                </Button>
              </Grid>
            </>
          )}

          {/* Display the fetched conversion rates in a table */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Current Conversion Rates:
            </Typography>
            {isLoading ? (
              <CircularProgress />
            ) : Object.keys(conversionRates).length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Conversion Pair</TableCell>
                      <TableCell align="center">Rate</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(conversionRates).map(([pair, rate]) => {
                      const [from, to] = pair.split("_TO_");
                      const fromSymbol = currencySymbols.get(from) || "";
                      const toSymbol = currencySymbols.get(to) || "";
                      return (
                        <TableRow key={pair}>
                          <TableCell component="th" scope="row">
                            {from} {fromSymbol} → {to} {toSymbol}
                          </TableCell>
                          <TableCell align="center">{rate}</TableCell>
                          <TableCell align="center">
                            {canWrite ? (
                              <Stack direction="row" spacing={1}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleEditConversion(pair, rate)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleDeleteConversion(pair)}
                                >
                                  Delete
                                </Button>
                              </Stack>
                            ) : (
                              "N/A"
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography>No conversion rates available.</Typography>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default AnalyticsConversionRates;